import { useState } from 'react'
import { T } from '../strings'
import { mix } from '../lib/color'
import type { Theme } from '../theme'
import type { GameState } from '../game/types'
import {
  STICKER_TOTAL,
  level,
  stickers,
  tableMedals,
  totalAnswers,
  weekPlayed,
  xpInLevel,
} from '../game/selectors'
import ParentPanel from './ParentPanel'

interface Props {
  state: GameState
  theme: Theme
}

function MePanel({ state, theme }: Props) {
  const { list, unlocked } = stickers(state, theme)
  const inLevel = xpInLevel(state)
  const days = weekPlayed(state)
  const medals = tableMedals(state)
  const medalsDone = medals.filter((m) => m.done).length

  return (
    <div>
      <div className="card level-card">
        <div className="row">
          <span className="level-card__title">
            {T.level} {level(state)}
          </span>
          <span className="level-card__hint">
            {inLevel}/100 {T.toNextLevel(level(state) + 1)}
          </span>
        </div>
        <div className="bar">
          <div className="bar__fill" style={{ background: theme.acc, width: `${inLevel}%` }} />
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-card__value">🔥 {state.progress.streak}</div>
          <div className="stat-card__label">{T.streakWord(state.progress.streak)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value">{totalAnswers(state)}</div>
          <div className="stat-card__label">{T.solvedTotal}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value">
            {unlocked}/{STICKER_TOTAL}
          </div>
          <div className="stat-card__label">{T.myStickers}</div>
        </div>
      </div>

      <div className="label" style={{ marginTop: 20 }}>
        {T.thisWeek}
      </div>
      <div className="week-dots">
        {days.map((day) => (
          <div key={day.day} className="week-col">
            <div
              className="day-dot"
              style={{
                background: day.played ? theme.acc : theme.card,
                borderColor: day.today ? theme.acc2 : theme.line,
              }}
              aria-hidden
            >
              {day.played ? '🔥' : ''}
            </div>
            <div className="week-col__day" style={{ color: day.today ? theme.ink : theme.ink2 }}>
              {T.weekDays[day.day]}
            </div>
          </div>
        ))}
      </div>

      <div className="row" style={{ marginTop: 20, minHeight: 0 }}>
        <span className="label">{T.medalsTitle}</span>
        <span className="label">{T.medalsCount(medalsDone, medals.length)}</span>
      </div>
      <div className="medals">
        {medals.map((medal) => (
          <div
            key={medal.n}
            className="medal"
            style={{
              borderColor: medal.done ? theme.acc : theme.line,
              background: medal.done ? mix(theme.acc, 0.35) : theme.card,
              opacity: medal.done ? 1 : 0.6,
            }}
          >
            <div className="medal__n">{medal.done ? '🥇' : medal.n}</div>
            <div className="medal__pct">{medal.done ? `на ${medal.n}` : `${medal.pct}%`}</div>
          </div>
        ))}
      </div>

      <div className="label" style={{ marginTop: 20 }}>
        {T.myStickers}
      </div>
      <div className="awards-grid">
        {list.map((sticker, i) => (
          <div key={i} className="award-card" style={{ opacity: sticker.locked ? 0.55 : 1 }}>
            <div className="award-card__icon" style={{ background: sticker.bg }} aria-hidden>
              {sticker.icon}
            </div>
            <div className="award-card__title">{sticker.title}</div>
            <div className="award-card__hint">{sticker.hint}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Awards({ state, theme }: Props) {
  const [tab, setTab] = useState<'me' | 'parent'>('me')

  return (
    <div className="screen screen--pad">
      <h1 className="screen__title">{T.awardsTitle}</h1>

      <div className="seg">
        <button
          type="button"
          className="seg__item"
          aria-pressed={tab === 'me'}
          onClick={() => setTab('me')}
        >
          {T.tabMe}
        </button>
        <button
          type="button"
          className="seg__item"
          aria-pressed={tab === 'parent'}
          onClick={() => setTab('parent')}
        >
          {T.tabParent}
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'me' ? <MePanel state={state} theme={theme} /> : <ParentPanel state={state} theme={theme} />}
      </div>
    </div>
  )
}
